import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Building2 } from "lucide-react";
import { API } from "../api";
import { ActionButton, Confirmation, ErrorNote } from "./Feedback";

// A pending invitation and its accept / decline decision; declining also removes
// the membership the emailed link already granted.
export default function InvitationCard({ invitation, onDecided }) {
  const [pending, setPending] = useState(null); // "accept" | "decline"
  const [settled, setSettled] = useState(null);
  const [error, setError] = useState("");

  const decide = async (action) => {
    setPending(action);
    setError("");
    try {
      await API.post(`/api/orgs/invitations/${invitation.id}/${action}`);
      setSettled(action);
      // Hold the confirmation on screen long enough to be read, then let the
      // gate re-resolve and move the user along.
      setTimeout(() => onDecided(), 1100);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          `We could not ${action} this invitation. Please try again.`
      );
      setPending(null);
    }
  };

  const inviter = invitation.inviter_name || invitation.inviter_email;
  const org = invitation.org_name;
  const expires = invitation.expires_at ? new Date(invitation.expires_at * 1000) : null;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <AnimatePresence mode="wait" initial={false}>
        {settled ? (
          <motion.div
            key="settled"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-6"
          >
            <Confirmation
              title={
                settled === "accept" ? `You have joined ${org}` : `Invitation to ${org} declined`
              }
              detail={
                settled === "accept"
                  ? "Taking you to the registry…"
                  : "You will not be added to this organisation."
              }
            />
          </motion.div>
        ) : (
          <motion.div key="deciding" exit={{ opacity: 0 }} className="p-6">
            <div className="flex items-start gap-4">
              <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700">
                <Building2 size={18} strokeWidth={2.5} />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-base leading-relaxed text-slate-900">
                  {inviter ? (
                    <>
                      <span className="font-black">{inviter}</span> invites you to join{" "}
                      <span className="font-black">{org}</span>.
                    </>
                  ) : (
                    <>
                      You have been invited to join{" "}
                      <span className="font-black">{org}</span>.
                    </>
                  )}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  Accepting lets you register and manage datasources on behalf of {org}.
                </p>
                {expires && (
                  <p className="mt-2 text-xs text-slate-400">
                    Expires {expires.toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div className="mt-4">
                <ErrorNote>{error}</ErrorNote>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <ActionButton
                icon={Check}
                busy={pending === "accept"}
                busyLabel="Joining…"
                disabled={pending !== null}
                onClick={() => decide("accept")}
              >
                Accept invitation
              </ActionButton>
              <ActionButton
                variant="quiet"
                icon={X}
                busy={pending === "decline"}
                busyLabel="Declining…"
                disabled={pending !== null}
                onClick={() => decide("decline")}
              >
                Decline
              </ActionButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
